import React from 'react';
import { useState, useEffect, useFormContext, Hooks, Code, Form, Field, Name, Title, Info, Actions, Toast, API, Utility, Button, Files, Control, Messages, OptionType } from 'helpers/FormImports';
import { Recall } from 'models/RecallModel';
import AsyncSelect from 'react-select/async';
import { useSearchParams } from 'react-router-dom';

const RecallForm: React.FC = () => {
  const [formId, formName] = [Code.Form.Id.RECALL, Code.Form.Name.RECALL];
  const {keyId, activeId, setActiveId, loading, setLoading, message, setMessage, showMenu, setShowMenu, confirm, setConfirm, error, setError, menuPosition, setMenuPosition, contextMenuRef, iframeRef, formRef } = useFormContext();
  const [activeTab, setActiveTab] = useState<string>(Utility.Storage.GetTab(formName));
  const [cityOptions, setCityOptions] = Hooks.Form.Options();
  const [typeOptions, setTypeOptions] = Hooks.Form.Options();
  const [statusOptions, setStatusOptions] = Hooks.Form.Options();
  const [segmentOptions, setSegmentOptions] = Hooks.Form.Options();
  const [doctorOptions, setDoctorOptions] = Hooks.Form.Options();
  const [priorityOptions, setPriorityOptions] = Hooks.Form.Options();
  const [contactOptions, setContactOptions] = Hooks.Form.Options();
  const [formData, setFormData] = useState<Recall>({});

  const [searchParams] = useSearchParams();
  const clientId = searchParams.get(Code.UrlPara.Client);
   
  Form.Click.Outside([contextMenuRef],[() => setShowMenu(false),() => {}],true);
  const Privilege = Hooks.Privilege.List(formId);
  Hooks.Privilege.HasOpen(Privilege);
  
  useEffect(() => {
    Files.Json.API(Code.File.CITY, Code.Option.Common.NA, setCityOptions, setError);
    Files.Json.API(Code.File.OPTION+Code.Group.RECALL_TYPE, Code.Option.Common.NA, setTypeOptions, setError);
    Files.Json.API(Code.File.OPTION+Code.Group.RECALL_STATUS, Code.Option.Common.NA, setStatusOptions, setError);
    Files.Json.API(Code.File.SEGMENT, Code.Option.Common.SELECT, setSegmentOptions, setError);
    Files.Json.API(Code.Form.Name.DOCTOR, Code.Option.Common.ALL, setDoctorOptions, setError);
    Files.Json.API(Code.File.OPTION+Code.Group.INQUIRY_CONTACT, Code.Option.Common.NOTHING, setContactOptions, setError);
    Files.Json.Http(Code.File.PRIORITY, Code.Option.Common.NA, setPriorityOptions, setError);
   
    API.Data.GetByKeyId<Recall>(formName, keyId, setLoading, setError)
      .then(async(data) => { if(data) {
        setActiveId(data.recall_active); 
        const clientData = await API.Data.getClientNameNumber(data.client_id);
        const formData = await Form.Map.Data(data);
        setFormData((prevData) => ({
          ...prevData,
          ...formData,
          ...clientData, 
        }));
        setLoading(false);
      }})

      const onNewEntry = async () => {
        if(keyId===Code.Key.NEW && clientId) {
          const clientName = await API.Data.GetClientName(Number(clientId));
          setFormData((prevData) => ({
            ...prevData,
            client_id: Number(clientId),
            client_name: Utility.Value.Text(clientName), 
          }));
        }
        setLoading(false);
      };
      onNewEntry();
  }, []);

  const onSubmit = async (e: React.FormEvent) => {
    if (!Toast.Validate.Form(e,setMessage)) return;
    const addonData = await API.Data.AddonData(formData, formName, keyId);
    const finalData = Utility.Sanitize.FormData({
      ...formData,
      ...addonData,
    });

    finalData.recall_call_date = Utility.Convert.DateOrToday(finalData.recall_call_date);
    finalData.recall_call_time = Utility.Convert.TimeOrNow(finalData.recall_call_time);

    API.Data.Save(formName, keyId, finalData, setMessage)
  };

  return (
    <Control.Form.Frame setMenuPosition={setMenuPosition} setShowMenu={setShowMenu}>
      <Messages.Toast Messages={message} Error={error} />
      <Messages.Confirm Messages={confirm} onConfirm={() => API.Data.Delete(formName, keyId, activeId, setMessage)} onRefuse={()=>{}} />
      {!loading && (
        <form id={Code.Form.Name.EDIT} ref={formRef} onSubmit={onSubmit} autoComplete="off">
          <Button.Save keyId={keyId} activeId={activeId} handleDelete={() => Toast.Delete.CurrentRecord(activeId, setConfirm)} clientNumber={formData.client_number} formName={formName} Privilege={Privilege} />
          <Control.Tab.Header titles={["Recall"]} activeTab={activeTab} formName={formName} setActiveTab={setActiveTab} />
          <Control.Form.Data>
          
            <Control.Tab.Content id={Code.Tab.Tab1} isActive={activeTab === Code.Tab.Tab1}>
              <Control.Row>
                <Field.Select id={Name.Common.TypeId} value={formData.type_id} title={Title.Type} options={typeOptions}  onChange={(e) => Form.Field.Change(e, formData, setFormData)}  size={[0,50,1]} />
                <Field.Select id={Name.Common.StatusId} value={formData.status_id} title={Title.State} options={statusOptions}  onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,50,0]} />
              </Control.Row>
              <Control.Row>
                <Field.Select id={Name.Common.SegmentId} value={formData.segment_id} title={Title.Segment} options={segmentOptions}  onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[0,50,1]} selected={Code.Option.Segment.OPTICAL} />
                <Field.Select id={Name.Doctor.Id} value={formData.doctor_id} title={Title.Doctor} options={doctorOptions}  onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,50,0]} />
              </Control.Row>
              <Control.Row>
                <Field.Header id={Name.Client.Id} title={Title.Client} require={true}>
                  <AsyncSelect id={Name.Client.Id} loadOptions={(inputValue) => API.Data.AsyncOption(inputValue, Code.Form.Name.CLIENT, Code.Mapping.client.value, Code.Mapping.client.label)} styles={Code.Style.FormOption} onChange={(selected) => Utility.Handle.ClientChange(selected, setFormData)} value={Utility.Value.Async(formData.client_id,formData.client_name)} placeholder="Search" />
                </Field.Header>
              </Control.Row>
              <Control.Row>
                <Field.Date id={Name.Recall.Date} value={formData.recall_date} title={Title.Date} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[0,50,1]} require={true} currentDate={true} />
                <Field.Time id={Name.Recall.CallTime} value={formData.recall_call_time} title={Title.Time} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,50,0]} require={true} currentTime={true} />
              </Control.Row>
              <Control.Row>
                <Field.Area id={Name.Recall.Comment} value={formData.recall_comment} title={Title.Comment} onChange={(e) => Form.Field.Change(e, formData, setFormData)} />
              </Control.Row>
              <Control.Row>
                <Field.Select id={Name.Recall.PriorityId} value={formData.priority_id} title="Interested" options={priorityOptions}  onChange={(e) => Form.Field.Change(e, formData, setFormData)}  size={[0,50,1]} require={true} />
                <Field.Select id={Name.Common.ContactMode} value={formData.contact_mode_id} title="Prefer to contact" options={contactOptions}  onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,50,0]} />
              </Control.Row>
            </Control.Tab.Content>
          
          </Control.Form.Data>
        </form>
      )}
      {showMenu && (<div ref={contextMenuRef}><Control.FormContextMenu keyId={keyId} activeId={activeId} showMenu={showMenu} formName={formName}  handleDelete={() => Toast.Delete.CurrentRecord(activeId, setConfirm)} setShowMenu={setShowMenu} Privilege={Privilege} pos={menuPosition} /></div>)}
      <Actions.Form />
    </Control.Form.Frame>
  );
};

export default RecallForm;